// Gift Guide Hub search bar (THE-586). Hand-mirrored from the BE handler at
// `thea-serverless/functions/theaWebCommon/gift_search.py` — no JSON schema in
// `thea-shared-schemas` yet, same posture as theaWebSimilarProducts: the
// request facets are validated on both sides, and the product
// half of the response reuses the existing RecommendationProduct shape.
//
// WHAT THIS OP IS. Two Typesense queries — one over `products`, one over
// `guides` — and nothing else. No LLM, no curation, no reranking. It is a
// callable at all only because the Typesense search key cannot ship to a
// browser. The hub must NOT search through `mutateRecommendation` /
// `getCarouselFeed`: those are Gemini agent runs on a 3-5s budget, one
// measured at 117.83s cold, and pointing a window-shopping search bar at them
// would make the cheapest-intent surface in the product the most expensive one
// we run (THE-577 plan §2).
//
// TWO REQUEST FIELDS ARE PRESENCE-SENSITIVE (THE-833), and for both of them
// omitting the key is the LEGACY request rather than a neutral default:
//   * `page`     — omit it and the result set stays capped at one page.
//   * `priceMax` — omit it and the server parses a cap out of your text;
//                  send `null` to clear that cap.
// Both are documented on the fields. Neither can be expressed by "send
// undefined", because `JSON.stringify` drops those keys entirely.
import type { RecommendationProduct } from '../recommendation';

/** The 10 DetailedAgeGroup bands, which are the QUIZ's own vocabulary — the
 * visitor met these exact labels there. Both indexes carry them
 * (`detailed_age_groups` on products, `ageBands` on guides), so one value
 * filters both. Do not invent new bands for the hub. */
export const GIFT_SEARCH_AGE_GROUPS = [
  'NEWBORN_0_2_MONTHS',
  'YOUNG_INFANT_2_6_MONTHS',
  'OLDER_INFANT_6_12_MONTHS',
  'TODDLER_1_3_YEARS',
  'PRESCHOOL_3_5_YEARS',
  'ELEMENTARY_6_10_YEARS',
  'MIDDLE_SCHOOL_11_14_YEARS',
  'HIGH_SCHOOL_14_18_YEARS',
  'YOUNG_ADULT',
  'OLDER_ADULT',
] as const;

export type GiftSearchAgeGroup = (typeof GIFT_SEARCH_AGE_GROUPS)[number];

export const GIFT_SEARCH_SORTS = ['relevance', 'price_asc', 'price_desc'] as const;

export type GiftSearchSort = (typeof GIFT_SEARCH_SORTS)[number];

/** What KIND of query this was, decided server-side.
 *
 * `navigational` — `trending`, `popular`, `new`: the visitor asked for a
 * RANKING, not a semantic match, and retrieval cannot serve it at any quality.
 * Render the curated row from `theaWebHubTrending/current` (THE-582) instead of
 * the empty state. The hub reads that document from Firestore directly; this
 * op deliberately does NOT inline it, so there is no extra read on the search
 * path and no second copy of a row the hub already renders.
 *
 * `search` — everything else.
 *
 * WHY THIS IS A FIELD AND NOT A CLIENT-SIDE STRING TEST. `trending` (0.265) and
 * `dark academia` (0.247) are BOTH `confidence: 'low'` and want opposite
 * treatments — the curated row, and a genuine empty state. The verdict cannot
 * tell them apart. Matching the words here would mean a second copy of the
 * navigational list in TypeScript, drifting from the server's. Measured live:
 * `trending`/`popular` come back `navigational` + `low`, `dark academia` comes
 * back `search` + `low`. */
export type GiftSearchIntent = 'navigational' | 'search';

/** A constraint the shopper stated as an EXCLUSION, which the search did not
 * apply — detected and reported rather than satisfied.
 *
 * Embeddings have no "not", and the negated word is usually the strongest
 * signal in the sentence, so it dominates the match. Measured live:
 * `girly but not pink` returns a Naked3 soft pink palette, pink Nike Dunks and
 * a Pink Champagne tea — 4 of 5 hits — and `gift card alternative` returns a
 * Nordstrom gift card.
 *
 * WHY THE SERVER CANNOT JUST FILTER IT OUT. The only exclusion the index
 * supports is over the fixed chip interest taxonomy (Cooking, Jewelry, Books,
 * Fitness ...). `products` has no colour, style or clutter field at all, so not
 * one of the real cases is expressible. Guessing would delete good results on
 * the strength of one word. The retrieval eval's prescription is verbatim
 * "detect it, don't try to satisfy it".
 *
 * WHY IT IS ITS OWN FIELD. Neither of the other two can carry it, and both
 * failures are measured: `relevance.confidence` comes back `'high'` on all four
 * of the queries above (0.4354, 0.506, 0.5411, 0.5718) because an inverted
 * negation matches its topic *well*; and `query.intent` is `'search'`, because
 * `girly but not pink` is an ordinary search. Re-deriving it in the client would
 * be a second copy of a server-side text rule — the drift `intent` exists to
 * avoid. */
export interface TheaWebSearchGiftsNegation {
  /** The excluded phrases, in query order — `['pink']`, `['clutter']`,
   * `['gift card']`. Safe to render in copy: phrasings that only LOOK like
   * exclusions are rejected server-side rather than passed through, so this
   * never carries `'sure'` from `not sure what to get` or `'more'` from
   * `no more than $50` (that one arrives as `priceMax` instead). */
  terms: string[];
  /** How many of the returned `products` literally contain one of `terms`.
   *
   * A LOWER BOUND — **do not gate the warning on this being > 0.** It is a
   * lexical scan, and the cases it misses are the eval's own: `doesn't like
   * clutter` returns a Customized Wooden Toy Box on Wheels and scores **0**,
   * because no product's text contains the word "clutter"; `not into sports`
   * returns a Football Hamper and a Soccer Ball bag and scores **1 of 5**.
   *
   * So the signal to branch on is the presence of `negation` itself. Use this
   * only to strengthen the message when there is evidence to point at
   * ("4 of these still mention pink"). */
  literalMatches: number;
}

export interface TheaWebSearchGiftsRequest {
  /** Free text. Required; whitespace-only is rejected with INVALID_ARGUMENT. */
  query: string;
  /** Age band facet. An unrecognised value is DROPPED, not rejected, so a
   * stale bundle sending a retired band gets unfiltered results. */
  ageGroup?: GiftSearchAgeGroup;
  /** OR of bands in generated order. Send this alone; the backend unions the legacy scalar. */
  ageGroups?: GiftSearchAgeGroup[];
  /** Explicit price ceiling from the facet, `null` to CLEAR a parsed one.
   *
   * THIS FIELD IS PRESENCE-SENSITIVE (THE-833) and the three states are not
   * interchangeable:
   *
   *   key absent   -> the server parses a cap out of `query` ("under $50",
   *                   "cheap" -> $25). This is the legacy request.
   *   `null`       -> NO CAP. Clears whatever the server would have parsed,
   *                   and echoes `priceMaxSource: 'cleared'`.
   *   `1`..`10000` -> that ceiling. Overrules any cap parsed from the text —
   *                   the visitor who picked a chip has overruled what they
   *                   typed.
   *
   * **The JS hazard, stated because it is easy to get wrong:**
   * `JSON.stringify` DROPS a key whose value is `undefined` and KEEPS one
   * whose value is `null`. So `{priceMax: undefined}` is the legacy
   * "parse my text" request and `{priceMax: null}` is the clear. Building the
   * payload with a spread like `...(cap ? {priceMax: cap} : {})` therefore
   * CANNOT clear — it omits the key. Send the literal `null`.
   *
   * **`0` and `Number.MAX_SAFE_INTEGER` do NOT clear.** Both are out of range,
   * so both fall back to parsing the text — which is what they did before
   * THE-833 too, so no behaviour changed. `null` is the only clear. (`0` read
   * literally is `price <= 0`, which would EMPTY the grid rather than widen
   * it, so it is a genuinely ambiguous spelling rather than a missing alias.)
   *
   * Clearing does not disturb the typed query: the text still ranks the
   * results, `query.text` still echoes it, and the negation warning still
   * fires. Only the price filter goes. */
  priceMax?: number | null;
  /** Default `relevance`. An unrecognised value falls back to it. */
  sort?: GiftSearchSort;
  /** PAGE SIZE, default 24, capped at 50. Not the result-set size — see
   * `page`. */
  limit?: number;
  /** 1-based page (THE-833). Omit it and you get one page and nothing else.
   *
   * SENDING `page: 1` IS A DIFFERENT REQUEST FROM OMITTING `page`, and this is
   * the one rule to get right. The products search is hybrid, and its vector
   * arm's `k` is what bounds the result set — a request with no `page` keeps
   * today's narrow `k`, which is why it has no page 2 to fetch (measured on
   * prod: `out_of` 8409, `found` 26). Sending `page` widens the candidate pool
   * so the walk has somewhere to go.
   *
   * **So send `page` on EVERY request of a paginated search, including the
   * first, or on none of them.** The two pools rank differently: measured over
   * 7 live queries, page 1 at the narrow `k` and page 1 at the wide one come
   * back in a different order on 5 of them, and `dark academia` shares only 11
   * of its 24 items. Fetching page 1 without `page` and page 2 with it slices
   * two different lists, which is exactly how a grid renders a duplicate.
   *
   * Out-of-range values CLAMP rather than fail (page 0 -> 1, page 400 ->
   * `pagination.maxPage`), and `pagination.page` echoes what was served.
   * A non-numeric value is read as "no pagination". */
  page?: number;
}

/** What the server actually applied, echoed back. Render it: a price cap the
 * visitor never asked for is the same trust problem as a constraint silently
 * dropped, which is the failure mode the retrieval eval found most common. */
export interface TheaWebSearchGiftsQueryEcho {
  text: string;
  /** See `GiftSearchIntent`. Check this BEFORE `relevance.confidence` when
   * choosing an empty state — a navigational query is not a failed search. */
  intent: GiftSearchIntent;
  priceMax: number | null;
  /** Where the applied cap came from.
   *
   * `query` — parsed out of the text ("under $50", "cheap").
   * `request` — the caller sent a numeric `priceMax`.
   * `cleared` — the caller sent `priceMax: null` (THE-833). `priceMax` is
   *   `null` and that was ASKED FOR.
   * `null` — no cap, and none was asked for either way.
   *
   * The last two both carry `priceMax: null`, and the hub needs to tell them
   * apart to restore its own filter state on a back-navigation: "the shopper
   * dismissed the chip" and "no chip was ever offered" are otherwise the same
   * response. If you switch exhaustively on this, `'cleared'` is new. */
  priceMaxSource: 'query' | 'request' | 'cleared' | null;
  /** Legacy echo: populated only for one band. Read ageGroups for selection. */
  ageGroup: GiftSearchAgeGroup | null;
  ageGroups: GiftSearchAgeGroup[];
  sort: GiftSearchSort;
  /** `null` when the shopper ruled nothing out — so the check is this field's
   * presence, not a count inside it. Non-null means a constraint was SEEN AND
   * NOT APPLIED, and the results may contain exactly what was excluded. See
   * `TheaWebSearchGiftsNegation`. */
  negation: TheaWebSearchGiftsNegation | null;
}

/** The server-side relevance verdict. READ THE CAVEAT BELOW BEFORE USING IT.
 *
 * Why it exists: `results.length === 0` can NEVER fire. The products search
 * carries a vector arm with `k`, so it always returns a full page, and the
 * guides search returns a fixed count whatever the filter. Measured over 53
 * queries, products never came back with fewer than 20 hits. A count-based
 * empty state is therefore unreachable, and the hub would confidently render
 * 20 irrelevant products for `dark academia`. So the gate is a score, and it
 * lives server-side where the scores are.
 *
 * WHAT IT DETECTS: "the catalogue has no vocabulary for this" — `dark academia`
 * 0.247, `arrives tomorrow` 0.259, the real logged query `trending` 0.265.
 *
 * WHAT IT DOES NOT DETECT: a dropped constraint or an inverted negation. Those
 * score HIGH by construction, because the query matches the topic while
 * ignoring the one thing the user specified — `gift card alternative` returns
 * gift cards at 0.541, the highest score of any query the eval judged bad.
 * `confidence: 'high'` does NOT mean "this answered the question." */
export interface TheaWebSearchGiftsRelevance {
  /** Cosine similarity of the best vector hit, or null when no hit carried a
   * distance (a pure keyword page). Surfaced so the floor can be re-tuned
   * from real traffic without a backend deploy.
   *
   * **`null` on any `page` past the first** — see `confidence`. Do not feed
   * deep-page samples into a floor re-tune; they were never query-level
   * verdicts. */
  topSimilarity: number | null;
  /** `unknown` means "we could not judge" and must be treated as `high` —
   * it fails OPEN, to results rather than to an empty state.
   *
   * **ALWAYS `unknown` on `page` 2+ (THE-833).** The verdict is a judgement
   * about the QUERY and can only be read off the first page: it comes from the
   * best vector hit among the results in front of it, and on a continuation
   * page those are by construction the ones that ranked worst. Measured on
   * prod, `gifts for dad` falls 0.5872 (page 1) → 0.5150 → 0.4936 → 0.4273 →
   * 0.3804 (page 8), heading toward the 0.35 floor. Reporting that would let a
   * deep page of a perfectly good search claim the catalogue has no vocabulary
   * for it.
   *
   * So the no-results state is a PAGE-1 decision. Keep page 1's verdict in
   * component state if you need it while paging; do not re-evaluate the gate
   * against a continuation response. */
  confidence: 'high' | 'low' | 'unknown';
  /** The floor `confidence` was computed against, echoed so a client never
   * hardcodes a copy of it. Present on every page. */
  floor: number;
}

/** The window this response is a slice of (THE-833). `null` when the caller
 * did not send `page` — a non-paginating request cannot reach page 2 (its `k`
 * is too narrow for there to be one), so reporting a `hasMore` for it would be
 * a claim about a page it cannot fetch.
 *
 * THERE IS NO TOTAL COUNT, deliberately, and one should not be added. The
 * products search is hybrid, so Typesense's `found` is dominated by the vector
 * arm's `k` and reads ~251 for almost every query at the current pool size —
 * it says nothing about how many products actually matched. `hasMore` is exact
 * instead: the server fetches one item more than it serves and reports whether
 * that peek found anything. */
export interface TheaWebSearchGiftsPagination {
  /** The page actually served, 1-based. May differ from what was requested:
   * out-of-range values clamp rather than fail, so a stale deep link lands on
   * the last real page instead of an error card. Trust this over your own
   * counter. */
  page: number;
  /** The page size actually used (the request's `limit`, defaulted/capped). */
  limit: number;
  /** Whether a further page exists. Exact — from an over-fetch, not a count.
   * Already accounts for `maxPage`, so it goes false on the deepest servable
   * page rather than offering a "load more" that would re-serve it. */
  hasMore: boolean;
  /** The deepest page this search can serve at this `limit`. Echoed so no
   * client repeats the arithmetic — it moves with `limit` (11 pages of 24, 5
   * of 50) because the server's candidate pool is a fixed number of ITEMS. */
  maxPage: number;
}

/** A guide beside the items. SECONDARY, deliberately: measured on the
 * retrieval eval, guides rescue 1 of the 14 queries where products come back
 * thin — 7%. They score well on exactly the axes products already handles
 * (occasion, recipient, interest) and add nothing where products fails (price,
 * delivery, negation, aesthetic vocabulary), because the 58 guides are indexed
 * on those same three axes. Leading with them would spend the best slot on the
 * page to fix 7% of the tail.
 *
 * ALREADY GATED SERVER-SIDE, by BOTH of production's guide gates — do not
 * re-filter. An empty array means "nothing good enough", not "nothing tried";
 * `guidesRelevance` says which. */
export interface TheaWebSearchGiftsGuide {
  slug: string;
  title: string;
  description: string;
  similarity: number;
  productCount: number | null;
}

/** What the guide gates did. Both run server-side, against `guide_matcher`'s
 * own constants, so the two thresholds cannot drift into a second language.
 *
 * BOTH GATES APPLY, and neither is redundant — measured live:
 *   `birthday gift for my sister` 0.3579 / 0.3474 / 0.3379 / 0.3018
 *       → the FLOOR does the work; 3 dropped, 1 shown.
 *   `coffee lover` 0.6917 / 0.629 / 0.4812 / 0.3938
 *       → all four clear the floor; the MARGIN drops the last two, leaving the
 *         two guides actually about coffee rather than a strip whose fourth
 *         tile is `bookworm-mom`.
 *
 * ONE CAVEAT, so the counts are not over-read: when the whole candidate set is
 * uniformly mediocre the margin passes most of it, because it asks "near the
 * best we found", not "good". `cheap stocking stuffers` returns 4 guides at
 * 0.37-0.39 that are generic. A high `returned` is not evidence of a match. */
export interface TheaWebSearchGiftsGuidesRelevance {
  /** Best similarity among guides that cleared the floor; null if none did. */
  topSimilarity: number | null;
  /** `guides.length`, restated so the counts read as one set. */
  returned: number;
  /** How many hits the gates were run over. 0 means the guides search
   * returned nothing or was unavailable — NOT that everything was rejected. */
  considered: number;
  /** `considered - returned`. Distinguishes "we found four and none were
   * close enough" from "the corpus had nothing" once `guides` is empty; those
   * are different sentences in the copy. */
  dropped: number;
  droppedBelowFloor: number;
  droppedBelowMargin: number;
  /** Echoed so no client hardcodes a copy. */
  floor: number;
  margin: number;
}

export interface TheaWebSearchGiftsResponse {
  query: TheaWebSearchGiftsQueryEcho;
  /** The answer. Items lead. Same shape carousel products use, so these run
   * through `productToCardItem` with identical image/click-out logic.
   *
   * Exactly one page of them — `limit` items, or fewer on the last page. */
  products: RecommendationProduct[];
  /** `null` unless the request sent `page`. See the interface. */
  pagination: TheaWebSearchGiftsPagination | null;
  relevance: TheaWebSearchGiftsRelevance;
  /** Up to 4, and often 0. A garnish, not a safety net — see the interface.
   *
   * STABLE ACROSS PAGES, so the guides row above the grid does not reshuffle
   * under a shopper paging it: the guides search never sees the page, and it
   * takes no price facet either, so neither paging nor clearing a cap can move
   * it. Render it once from page 1 and leave it alone. */
  guides: TheaWebSearchGiftsGuide[];
  guidesRelevance: TheaWebSearchGiftsGuidesRelevance;
}
